import type {IssueAgentTask, IssueAssignedAgentInfo} from '../contexts/IssueAgentTasksContext'
import type {SessionState} from '@github-ui/agent-sessions/types/session'
import {isSessionState} from '@github-ui/agent-sessions/types/session'
import {getLeadingVisualIconAttributes} from '@github-ui/agent-sessions/utils/get-leading-visual-icon-attributes'
import {getGitHubArtifactData} from '@github-ui/agent-sessions/utils/task-artifact-helpers'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {useNavigate} from '@github-ui/use-navigate'
import {ActionList, RelativeTime, Stack, Truncate} from '@primer/react'
import {useCallback} from 'react'
import {TASK_NOUN} from '../utils/constants'

import type {LinkedIssuePullRequest} from '../utils/types'
import {getPRStatusIcon} from './PRStatusIcon'
import styles from './TaskList.module.css'

type TaskListProps = {
  tasks: IssueAgentTask[]
  assignedAgents: IssueAssignedAgentInfo[]
  linkedPullRequests: LinkedIssuePullRequest[]
  onSelectTask?: (task: IssueAgentTask) => void
}

function getTaskStateLabel(state: string, pullRequest: LinkedIssuePullRequest | null): string {
  if (state === 'waiting_for_user') return 'Needs input'
  if (state === 'completed') {
    if (!pullRequest) return 'Completed'

    if (pullRequest.state === 'OPEN') return 'Ready for review'
    if (pullRequest.state === 'MERGED') return 'Merged'
    if (pullRequest.state === 'CLOSED') return 'Closed'

    return 'Completed'
  }
  if (state === 'failed' || state === 'timed_out') return 'Failed'
  return 'In progress'
}

function TaskListItem({
  task,
  assignedAgents,
  linkedPullRequests,
  onSelectTask,
}: {
  task: IssueAgentTask
  assignedAgents: IssueAssignedAgentInfo[]
  linkedPullRequests: TaskListProps['linkedPullRequests']
  onSelectTask?: (task: IssueAgentTask) => void
}) {
  const sidePanelEnabled = isFeatureEnabled('issue_cca_task_side_panel')
  const taskState = task.state ?? task.status ?? 'in_progress'
  const state: SessionState = isSessionState(taskState) ? taskState : 'in_progress'
  const title = task.name || `New ${TASK_NOUN}`

  const pullRequestId = getGitHubArtifactData(task.artifacts)?.id
  const pullRequest = linkedPullRequests.find(linkedPr => linkedPr.databaseId === pullRequestId) ?? null
  const leadingVisualAttributes = getLeadingVisualIconAttributes(
    pullRequest?.state?.toLowerCase() ?? 'open',
    false,
    state,
    !!task.archived_at,
  )
  const agentInfo = assignedAgents.find(agent => task.agent_collaborators?.some(c => c.agent_id === agent.id))
  const taskUrl = task.url
  const updatedAt = task.updated_at ?? task.last_updated_at ?? task.created_at
  const navigate = useNavigate()

  const handleSelect = useCallback(() => {
    if (sidePanelEnabled) {
      onSelectTask?.(task)
      return
    }

    if (!taskUrl) return

    navigate(taskUrl)
  }, [navigate, onSelectTask, sidePanelEnabled, task, taskUrl])

  const leadingVisualContent = (
    <div
      style={{
        color: leadingVisualAttributes.color,
      }}
    >
      <leadingVisualAttributes.icon />
    </div>
  )

  const titleContent = (
    <Truncate title={title} maxWidth="100%">
      {title}
    </Truncate>
  )

  const descriptionContent = (
    <>
      {getTaskStateLabel(taskState, pullRequest)}
      {updatedAt && (
        <>
          {' · '}Updated <RelativeTime tense="past" datetime={updatedAt} format="relative" />
        </>
      )}
    </>
  )

  const trailingVisualContent = (
    <Stack direction="horizontal" align="center" justify="end" gap="normal" className={styles.trailingContent}>
      {pullRequest && (
        <a
          onClick={e => e.stopPropagation()}
          href={pullRequest.url}
          aria-label={`View pull request #${pullRequest.number}`}
          className={styles.pullRequest}
        >
          {getPRStatusIcon(pullRequest.state)}
          <span>#{pullRequest.number}</span>
        </a>
      )}
      {agentInfo?.avatar_url && (
        <GitHubAvatar src={agentInfo?.avatar_url} size={16} square alt={agentInfo?.display_name || 'Agent'} />
      )}
    </Stack>
  )

  return (
    <ActionList.Item className={styles.item} onSelect={handleSelect}>
      <ActionList.LeadingVisual>{leadingVisualContent}</ActionList.LeadingVisual>
      {titleContent}
      <ActionList.Description variant="block" className={styles.description}>
        {descriptionContent}
      </ActionList.Description>
      <ActionList.TrailingVisual>{trailingVisualContent}</ActionList.TrailingVisual>
    </ActionList.Item>
  )
}

export function TaskList({tasks, assignedAgents, linkedPullRequests, onSelectTask}: TaskListProps) {
  return (
    <ActionList>
      {tasks.map((task: IssueAgentTask) => (
        <TaskListItem
          key={task.id}
          task={task}
          assignedAgents={assignedAgents}
          linkedPullRequests={linkedPullRequests}
          onSelectTask={onSelectTask}
        />
      ))}
    </ActionList>
  )
}
